import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { SearchInput } from "@/components/molecules/SearchInput";
import { Tooltip } from "@/components/atoms/Tooltip";
import { FILTER_ACTIVE_CLASS, FilterBarBoundary, useFilterHighlight } from "@/components/molecules/FilterBar";

export type MenuSelectOption = {
  value: string;
  label: string;
  /** Closed-trigger text when this option is selected (dropdown still shows {@link label}). */
  triggerLabel?: string;
  /** Optional secondary line rendered beneath the label inside the dropdown option. */
  subtitle?: string;
  /** Optional element rendered before the label (e.g. a status dot or icon). */
  leading?: React.ReactNode;
  depth?: number;
  disabled?: boolean;
  disabledTooltip?: string;
};

export const MenuSelect: React.FC<{
  label?: string;
  /** Single-select value. Optional so a multi-select can leave it out entirely. */
  value?: string;
  onChange?: (v: string) => void;
  /**
   * Turns the control into a multi-select: options toggle rather than replace, the
   * panel stays open while picking, and the picked ones float to the top of the list
   * so a selection made across several searches can still be reviewed in one place.
   */
  multiple?: boolean;
  /** Selected values in multi mode. Ignored when {@link multiple} is false. */
  values?: string[];
  onValuesChange?: (next: string[]) => void;
  /** Trigger text once more than one option is picked. Defaults to "N selected". */
  multiSummary?: (count: number) => string;
  options: MenuSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  hideLabel?: boolean;
  /** Type-ahead filter inside the dropdown; filter text is debounced to limit updates while typing. */
  enableSearch?: boolean;
  /** Alias for {@link enableSearch} (backward compatible). */
  searchable?: boolean;
  searchDebounceMs?: number;
  searchPlaceholder?: string;
  onDropdownSearchQueryChange?: (trimmedQuery: string) => void;
  searchInTrigger?: boolean;
  /** Called when the user scrolls near the end of the dropdown list. */
  onDropdownEndReached?: () => void;
  /** Called when the dropdown is opened. */
  onOpen?: () => void;
  /** Show loading state inside the dropdown list. */
  loading?: boolean;
  /** When false, infinite scroll will not request more pages. */
  hasMorePages?: boolean;
  /** Shows a red asterisk beside the field label. */
  required?: boolean;
  /** Overrides trigger chrome (border, padding, height) when the select sits inside a shared input shell. */
  triggerClassName?: string;
  /** Caps the panel height to roughly this many option rows before it scrolls, instead of the default ~5-6. */
  maxVisibleOptions?: number;
  /** Dropdown panel is at least this wide (px), even when the trigger is narrower. */
  minDropdownWidth?: number;
  /** Show the full selected label on hover when the trigger text is truncated. */
  showSelectedTooltip?: boolean;
}> = ({
  label,
  value = "",
  onChange,
  multiple = false,
  values,
  onValuesChange,
  multiSummary,
  options,
  placeholder = "Select…",
  disabled,
  hideLabel = false,
  enableSearch = false,
  searchable = false,
  searchDebounceMs = 250,
  searchPlaceholder = "Search…",
  onDropdownSearchQueryChange,
  searchInTrigger = false,
  onDropdownEndReached,
  onOpen,
  loading = false,
  hasMorePages = true,
  required = false,
  triggerClassName,
  maxVisibleOptions,
  minDropdownWidth,
  showSelectedTooltip = false,
}) => {
  const [open, setOpen] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [dropdownPlacement, setDropdownPlacement] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
    openUp: boolean;
  } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const triggerShellRef = useRef<HTMLDivElement>(null);
  const triggerInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLLIElement>(null);
  const showSearch = enableSearch || searchable;
  const comboboxSearch = showSearch && searchInTrigger;
  const selectedValues = useMemo(() => (multiple ? (values ?? []) : []), [multiple, values]);
  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

  const toggleValue = useCallback(
    (optionValue: string) => {
      if (!onValuesChange) return;
      onValuesChange(
        selectedSet.has(optionValue)
          ? selectedValues.filter((v) => v !== optionValue)
          : [...selectedValues, optionValue],
      );
    },
    [onValuesChange, selectedSet, selectedValues],
  );
  const debouncedFilter = useDebouncedValue(filterText.trim(), showSearch ? searchDebounceMs : 0);

  const allowServerSearchWhileEmpty = Boolean(showSearch && onDropdownSearchQueryChange);
  const serverSideSearch = Boolean(onDropdownSearchQueryChange);
  const canOpen = !disabled && (options.length > 0 || allowServerSearchWhileEmpty || Boolean(onOpen));

  useEffect(() => {
    if (!open || !showSearch || !onDropdownSearchQueryChange) return;
    onDropdownSearchQueryChange(debouncedFilter);
  }, [open, showSearch, debouncedFilter, onDropdownSearchQueryChange]);

  useEffect(() => {
    if (open) return;
    setFilterText("");
    onDropdownSearchQueryChange?.("");
  }, [open, onDropdownSearchQueryChange]);

  const visibleOptions = useMemo(() => {
    const filtered = (() => {
      if (!showSearch) return options;
      // Parent loads options via API; avoid re-filtering server results by label.
      if (serverSideSearch) return options;
      const q = debouncedFilter.toLowerCase();
      if (!q) return options;
      return options.filter(
        (o) =>
          o.label.toLowerCase().includes(q) ||
          o.value.toLowerCase().includes(q) ||
          (o.subtitle?.toLowerCase().includes(q) ?? false),
      );
    })();
    if (!multiple || selectedSet.size === 0) return filtered;
    // Picked options first. A multi-select is usually built up over several searches
    // and pages, so what is already picked has to stay reviewable without hunting.
    const picked = filtered.filter((o) => selectedSet.has(o.value));
    if (picked.length === 0) return filtered;
    return [...picked, ...filtered.filter((o) => !selectedSet.has(o.value))];
  }, [showSearch, serverSideSearch, options, debouncedFilter, multiple, selectedSet]);

  /** Index the divider is drawn after — the last pinned (already picked) option. */
  const pinnedCount = useMemo(
    () =>
      multiple && selectedSet.size > 0
        ? visibleOptions.filter((o) => selectedSet.has(o.value)).length
        : 0,
    [multiple, selectedSet, visibleOptions],
  );

  const updateDropdownPlacement = useCallback(() => {
    const el =
      triggerShellRef.current ??
      triggerRef.current ??
      rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewportPad = 8;
    const gap = 6;
    // Two-line options (label + subtitle, e.g. dial code + country name) need more
    // row height than plain single-line options.
    const optionRowHeight = options.some((o) => o.subtitle) ? 60 : 42;
    const searchBarHeight = showSearch && !comboboxSearch ? 52 : 0;
    const preferredMax = maxVisibleOptions
      ? maxVisibleOptions * optionRowHeight + searchBarHeight
      : showSearch && !comboboxSearch
        ? 256
        : 208;
    const minHeight = maxVisibleOptions ? Math.min(preferredMax, optionRowHeight + searchBarHeight) : 96;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPad;
    const spaceAbove = rect.top - viewportPad;
    // With a caller-capped panel, flip up once there isn't room to show the full
    // capped height below — not the fixed 120px threshold used for the default panel.
    const flipThreshold = maxVisibleOptions ? preferredMax : 120;
    const openUp = spaceBelow < flipThreshold && spaceAbove > spaceBelow;
    const maxHeight = Math.min(preferredMax, openUp ? spaceAbove - gap : spaceBelow - gap);
    setDropdownPlacement({
      top: openUp ? rect.top - gap : rect.bottom + gap,
      left: rect.left,
      width: Math.max(rect.width, minDropdownWidth ?? 0),
      maxHeight: Math.max(minHeight, maxHeight),
      openUp,
    });
  }, [showSearch, comboboxSearch, maxVisibleOptions, minDropdownWidth, options]);

  useLayoutEffect(() => {
    if (!open) {
      setDropdownPlacement(null);
      return;
    }
    updateDropdownPlacement();
    window.addEventListener("resize", updateDropdownPlacement);
    window.addEventListener("scroll", updateDropdownPlacement, true);
    return () => {
      window.removeEventListener("resize", updateDropdownPlacement);
      window.removeEventListener("scroll", updateDropdownPlacement, true);
    };
  }, [open, updateDropdownPlacement, visibleOptions.length]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const checkDropdownEndReached = useCallback(() => {
    if (!hasMorePages || loading) return;
    const el = listRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distance <= 48) onDropdownEndReached?.();
  }, [hasMorePages, loading, onDropdownEndReached]);

  useEffect(() => {
    if (!open || !dropdownPlacement || !onDropdownEndReached) return;
    const el = listRef.current;
    if (!el) return;
    el.addEventListener("scroll", checkDropdownEndReached);
    const id = requestAnimationFrame(() => checkDropdownEndReached());
    return () => {
      cancelAnimationFrame(id);
      el.removeEventListener("scroll", checkDropdownEndReached);
    };
  }, [open, dropdownPlacement, onDropdownEndReached, checkDropdownEndReached, visibleOptions.length, hasMorePages]);

  /** Observe the list sentinel — more reliable than scroll distance in portaled dropdowns. */
  useEffect(() => {
    if (!open || !dropdownPlacement || !onDropdownEndReached || loading || !hasMorePages) return;
    const root = listRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onDropdownEndReached();
        }
      },
      { root, rootMargin: "48px", threshold: 0 },
    );
    observer.observe(sentinel);
    const id = requestAnimationFrame(() => checkDropdownEndReached());
    return () => {
      cancelAnimationFrame(id);
      observer.disconnect();
    };
  }, [open, dropdownPlacement, onDropdownEndReached, loading, visibleOptions.length, hasMorePages, checkDropdownEndReached]);

  /** If the first page does not fill the list, load the next page until it scrolls or runs out. */
  useEffect(() => {
    if (!open || !dropdownPlacement || !onDropdownEndReached || loading || !hasMorePages) return;
    const id = requestAnimationFrame(() => {
      const el = listRef.current;
      if (!el) return;
      if (el.scrollHeight <= el.clientHeight + 1) {
        onDropdownEndReached();
      }
    });
    return () => cancelAnimationFrame(id);
  }, [open, dropdownPlacement, onDropdownEndReached, loading, visibleOptions.length, hasMorePages]);

  const selected =
    options.find((o) => o.value === value) ??
    (value ? options.find((o) => o.value.toLowerCase() === value.toLowerCase()) : undefined);

  const multiTriggerLabel =
    selectedValues.length === 0
      ? placeholder
      : selectedValues.length === 1
        ? (options.find((o) => o.value === selectedValues[0])?.label ?? selectedValues[0])
        : (multiSummary?.(selectedValues.length) ?? `${selectedValues.length} selected`);

  const selectedTriggerText =
    selected?.triggerLabel ?? selected?.label;

  const triggerLabel = multiple
    ? multiTriggerLabel
    : (selectedTriggerText ??
      (options.length === 0 && !allowServerSearchWhileEmpty
        ? placeholder
        : value.trim() || placeholder));

  /** No matching option — show faded style like an empty-ish state, but still render raw API text. */
  const showSubtleTrigger = multiple
    ? selectedValues.length === 0
    : options.length > 0 && !selectedTriggerText && !value.trim() && !allowServerSearchWhileEmpty;

  const unknownValueLabel = value.trim() && !selectedTriggerText ? value.trim() : "";

  const closedTriggerText = selectedTriggerText ?? unknownValueLabel;

  const triggerInputValue = open ? filterText : closedTriggerText;

  const triggerInputPlaceholder =
    unknownValueLabel && !selected?.label && !open ? unknownValueLabel : placeholder;

  const handleTriggerInputChange = (next: string) => {
    setFilterText(next);
    if (!open) setOpen(true);
    if (!multiple && value && selected?.label && next !== selected.label) onChange?.("");
  };

  const handleTriggerFocus = () => {
    if (!canOpen) return;
    setOpen((was) => {
      if (!was) onOpen?.();
      return true;
    });
    if (comboboxSearch && !filterText) setFilterText("");
  };

  const toggleOpen = () => {
    if (!canOpen) return;
    setOpen((was) => {
      const next = !was;
      if (next && comboboxSearch) {
        setFilterText("");
        requestAnimationFrame(() => triggerInputRef.current?.focus());
      }
      if (next) onOpen?.();
      return next;
    });
  };

  /** An empty value is the "Any …" option in filter bars, so only a real selection counts. */
  const filterActive = useFilterHighlight(multiple ? selectedValues.length > 0 : value.trim() !== "");

  const triggerTooltipLabel =
    typeof triggerLabel === "string" && triggerLabel.trim() !== "" && triggerLabel !== placeholder
      ? triggerLabel
      : "";

  const triggerShellClass = cn(
    "w-full flex items-center gap-2 min-h-[42px] pl-4 pr-3 py-2 text-left text-sm rounded-xl border bg-white shadow-sm outline-none transition-all",
    "border-bridge-border/80 hover:border-bridge-accent/35",
    filterActive && FILTER_ACTIVE_CLASS,
    open ? "ring-2 ring-bridge-accent/25 border-bridge-accent" : "",
    !canOpen && "opacity-60 cursor-not-allowed bg-bridge-bg/50",
    triggerClassName,
  );

  return (
    <div ref={rootRef} className="relative w-full self-start">
      {!hideLabel && label ? (
        <span className="block text-xs font-semibold text-bridge-muted uppercase tracking-wider mb-1.5">
          {label}
          {required ? <span className="text-red-500"> *</span> : null}
        </span>
      ) : null}
      {comboboxSearch ? (
        <div
          ref={triggerShellRef}
          className={triggerShellClass}
          onMouseDown={(e) => {
            if ((e.target as HTMLElement).closest("[data-chevron]")) return;
            if (!canOpen) e.preventDefault();
          }}
        >
          <input
            ref={triggerInputRef}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-autocomplete="list"
            disabled={!canOpen}
            value={triggerInputValue}
            placeholder={triggerInputPlaceholder}
            onChange={(e) => handleTriggerInputChange(e.target.value)}
            onFocus={handleTriggerFocus}
            className={cn(
              "min-w-0 flex-1 bg-transparent border-0 p-0 text-sm text-bridge-fg placeholder:text-bridge-subtle outline-none focus:ring-0",
              !open && unknownValueLabel && !selected?.label && "text-bridge-subtle",
            )}
            style={selected?.depth && !open ? { paddingLeft: selected.depth * 10 } : undefined}
          />
          <button
            type="button"
            data-chevron
            tabIndex={-1}
            disabled={!canOpen}
            aria-label={open ? "Close options" : "Open options"}
            onClick={toggleOpen}
            className="shrink-0 rounded p-0.5 text-bridge-muted hover:text-bridge-fg disabled:opacity-50"
          >
            <ChevronDown
              className={cn("w-4 h-4 transition-transform duration-200", open && "rotate-180")}
              aria-hidden
            />
          </button>
        </div>
      ) : (
        <button
          ref={triggerRef}
          type="button"
          disabled={!canOpen}
          aria-expanded={open}
          aria-haspopup="listbox"
          onClick={toggleOpen}
          className={triggerShellClass}
        >
          {selected?.leading ? (
            <span className="shrink-0 inline-flex items-center">{selected.leading}</span>
          ) : null}
          {showSelectedTooltip && triggerTooltipLabel ? (
            <Tooltip content={triggerTooltipLabel} side="top" hoverOnly className="flex-1 min-w-0">
              <span
                className={cn(
                  "truncate block w-full",
                  showSubtleTrigger || (loading && !selected)
                    ? "text-bridge-subtle"
                    : "text-bridge-fg font-medium",
                )}
                style={selected?.depth ? { paddingLeft: selected.depth * 10 } : undefined}
              >
                {triggerLabel}
              </span>
            </Tooltip>
          ) : (
            <span
              className={cn(
                "truncate flex-1",
                showSubtleTrigger || (loading && !selected)
                  ? "text-bridge-subtle"
                  : "text-bridge-fg font-medium",
              )}
              style={selected?.depth ? { paddingLeft: selected.depth * 10 } : undefined}
            >
              {triggerLabel}
            </span>
          )}
          {loading ? (
            <Loader2 className="shrink-0 w-4 h-4 text-bridge-muted animate-spin" aria-label="Loading" />
          ) : (
            <ChevronDown
              className={cn(
                "shrink-0 w-4 h-4 text-bridge-muted transition-transform duration-200",
                open && "rotate-180",
              )}
              aria-hidden
            />
          )}
        </button>
      )}

      {open &&
        canOpen &&
        dropdownPlacement &&
        createPortal(
          <FilterBarBoundary>
          <div
            ref={panelRef}
            role="presentation"
            // Marks this as a floating surface belonging to the control that
            // opened it, so an enclosing popover (e.g. a column filter) doesn't
            // treat a click on an option as a click outside itself.
            data-floating-surface
            className="fixed z-[200] flex flex-col rounded-xl border border-bridge-border/70 bg-white shadow-lg shadow-slate-200/80"
            style={{
              top: dropdownPlacement.top,
              left: dropdownPlacement.left,
              width: Math.max(
                dropdownPlacement.width,
                minDropdownWidth ?? 0,
                showSearch ? 200 : 0,
              ),
              maxHeight: dropdownPlacement.maxHeight,
              transform: dropdownPlacement.openUp ? "translateY(-100%)" : undefined,
            }}
          >
            {showSearch && !comboboxSearch ? (
              <div className="shrink-0 border-b border-bridge-border/70 bg-white p-2">
                <SearchInput
                  value={filterText}
                  onChange={setFilterText}
                  placeholder={searchPlaceholder}
                  autoFocus
                />
              </div>
            ) : null}
            {multiple && selectedValues.length > 0 ? (
              <div className="flex shrink-0 items-center justify-between border-b border-bridge-border/70 bg-bridge-bg/60 px-3 py-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-bridge-muted">
                  {selectedValues.length} selected
                </span>
                <button
                  type="button"
                  onClick={() => onValuesChange?.([])}
                  className="text-[11px] font-semibold text-bridge-muted hover:text-bridge-fg"
                >
                  Clear all
                </button>
              </div>
            ) : null}
            <ul
              ref={listRef}
              role="listbox"
              className="overflow-auto bg-white py-1"
              style={{
                maxHeight:
                  dropdownPlacement.maxHeight -
                  (showSearch && !comboboxSearch ? 52 : 0) -
                  // The "N selected / Clear all" strip above the list, when shown.
                  (multiple && selectedValues.length > 0 ? 30 : 0),
              }}
            >
              {loading && visibleOptions.length === 0 ? (
                <li className="px-3 py-3 text-center text-sm text-bridge-muted">Loading…</li>
              ) : visibleOptions.length === 0 ? (
                <li className="px-3 py-3 text-center text-sm text-bridge-muted">No matches</li>
              ) : (
                <>
                  {visibleOptions.map((opt, optIndex) => {
                    const isActive = multiple ? selectedSet.has(opt.value) : opt.value === value;
                    const isOptionDisabled = Boolean(opt.disabled);
                    // Separates the pinned (already picked) block from the rest of the list.
                    const isLastPinned = multiple && pinnedCount > 0 && optIndex === pinnedCount - 1;
                    const optionButton = (
                      <button
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        aria-disabled={isOptionDisabled}
                        tabIndex={isOptionDisabled ? -1 : 0}
                        onClick={() => {
                          if (isOptionDisabled) return;
                          if (multiple) {
                            // Picking is cumulative here, so the panel stays open
                            // for the next one.
                            toggleValue(opt.value);
                            return;
                          }
                          // Re-picking the current value is a no-op: notifying
                          // the caller would push an identical value back into
                          // state, and for a filter that means a fresh request
                          // for results that cannot have changed.
                          if (!isActive) onChange?.(opt.value);
                          setOpen(false);
                        }}
                        className={cn(
                          "w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors",
                          isOptionDisabled
                            ? "cursor-not-allowed text-bridge-muted opacity-50"
                            : isActive
                              ? "bg-bridge-accent/12 text-bridge-accent font-medium"
                              : "text-bridge-fg hover:bg-bridge-bg",
                          isLastPinned && "border-b border-bridge-border/70",
                        )}
                      >
                        {multiple ? (
                          <span
                            aria-hidden
                            className={cn(
                              "flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
                              isActive
                                ? "border-bridge-accent bg-bridge-accent text-white"
                                : "border-bridge-border bg-white",
                            )}
                          >
                            {isActive && <Check className="h-3 w-3" strokeWidth={3} />}
                          </span>
                        ) : null}
                        {opt.leading ? (
                          <span className="shrink-0 inline-flex items-center">{opt.leading}</span>
                        ) : null}
                        <span className="flex-1 min-w-0" style={{ paddingLeft: (opt.depth ?? 0) * 12 }}>
                          <span className="block truncate" title={opt.label}>
                            {opt.label}
                          </span>
                          {opt.subtitle ? (
                            <span
                              className={cn(
                                "block truncate text-[10px] uppercase tracking-widest mt-0.5",
                                isActive ? "text-bridge-accent/70" : "text-bridge-muted opacity-70",
                              )}
                            >
                              {opt.subtitle}
                            </span>
                          ) : null}
                        </span>
                        {!multiple && isActive && !isOptionDisabled && (
                          <Check className="shrink-0 w-4 h-4 text-bridge-accent" strokeWidth={2.5} />
                        )}
                      </button>
                    );
                    return (
                      <li key={opt.value || "__root__"} role="presentation">
                        {isOptionDisabled && opt.disabledTooltip ? (
                          <Tooltip content={opt.disabledTooltip} followCursor variant="muted">
                            {optionButton}
                          </Tooltip>
                        ) : (
                          optionButton
                        )}
                      </li>
                    );
                  })}
                  <li ref={sentinelRef} aria-hidden className="h-px shrink-0 pointer-events-none" />
                  {loading ? (
                    <li className="px-3 py-2 text-center text-xs text-bridge-muted">Loading more…</li>
                  ) : null}
                </>
              )}
            </ul>
          </div>
          </FilterBarBoundary>,
          document.body,
        )}
    </div>
  );
};
